/**
 * Client-fulfilled tool names — the single source of truth for which tool
 * names the server must treat as "no execute here; the browser supplies the
 * result via `addToolOutput`".
 *
 * Two namespaces exist today:
 *   - `app_<8hex>` — SEP-1865 app-provided tool aliases, generated by the
 *     client registry (`app-tools-registry.ts`) for tools an MCP app iframe
 *     advertised.
 *   - `ui_*` — MCPJam's own browser-fulfilled tools, hand-named in the
 *     client catalog (`client/src/lib/webmcp/ui-tools-catalog.ts`) and
 *     validated server-side by `validateUiToolEntries`.
 *
 * WHAT THE `ui_` PREFIX ASSERTS: only that the BROWSER resolves the call.
 * Not that the tool drives a screen — `ui_ask_user` renders a card and waits
 * for an answer — and NOT that it is a WebMCP tool: the catalog is
 * WebMCP-*shaped* but deliberately never exposed to browser-native agents
 * (`document.modelContext` / `navigator.modelContext`). The prefix is load
 * bearing precisely because it is this narrow: it is the token the server's
 * skip gate and pause predicate key on, so a browser-fulfilled tool named
 * anything else would either be executed server-side or leave the stream
 * waiting on a result nobody will send.
 *
 * Both the skip gate (`isSkippableClientFulfilledToolCall` in
 * `http-tool-calls.ts`) and the pause predicate
 * (`hasUnresolvedClientFulfilledToolCalls` in `mcpjam-stream-handler.ts`)
 * consume these so the two paths can never drift apart.
 */

export const APP_TOOL_ALIAS_REGEX = /^app_[a-z0-9]{8}$/i;

/**
 * UI tool names are curated, lowercase snake_case with a reserved `ui_`
 * prefix. Max 64 chars total — a strict subset of the Anthropic tool-name
 * charset (`^[a-zA-Z0-9_-]{1,64}$`).
 */
export const UI_TOOL_NAME_REGEX = /^ui_[a-z0-9][a-z0-9_]{0,60}$/;

export function isAppToolAlias(name: string): boolean {
  return APP_TOOL_ALIAS_REGEX.test(name);
}

export function isUiToolName(name: string): boolean {
  return UI_TOOL_NAME_REGEX.test(name);
}

/**
 * True when `name` belongs to a client-fulfilled namespace. Callers that gate
 * on "is this tool call resolved by the browser?" must use this rather than
 * matching one namespace's regex directly.
 */
export function isClientFulfilledToolName(name: string): boolean {
  return isAppToolAlias(name) || isUiToolName(name);
}

/**
 * MCP tool annotations, as defined by the protocol's `ToolAnnotations`.
 *
 * Only the hints MCPJam's first-party `ui_*` catalog sets are modelled. The
 * protocol's defaults are deliberately pessimistic — an absent
 * `destructiveHint` means "assume destructive" — which is what makes an
 * unannotated future tool fail safe rather than execute silently.
 *
 * TRUST BOUNDARY: annotations are hints, and the MCP spec requires clients to
 * treat them as untrusted unless the server is verified. This type — and the
 * approval policy below — apply ONLY to MCPJam's own curated `ui_*` catalog
 * and its server-validated snapshot. Never derive approval policy from a
 * third-party MCP server's annotations.
 */
export interface UiToolAnnotations {
  readOnlyHint?: boolean;
  destructiveHint?: boolean;
  idempotentHint?: boolean;
  openWorldHint?: boolean;
}

/**
 * Whether a UI tool call must pause for the user's approval this turn.
 *
 * Single source of truth for BOTH sides of the approval handshake: the
 * server (per-tool `needsApproval` in `buildUiTools`, and every engine's
 * approval gate via `classifyUiToolApprovals`) and the client (the
 * executor's defer-before-execute gate). They must agree because the client
 * decides "defer" when the tool-call chunk arrives — BEFORE the server's
 * approval-request chunk reaches it. If they disagree, turns strand.
 *
 * Two modes, keyed off the turn's `requireToolApproval` flag:
 *   - strict (flag on)  — every mutating UI tool gates.
 *   - default (flag off) — only DESTRUCTIVE UI tools gate. Actions the user
 *     watches happen in their own app don't need a confirmation click; the
 *     ones they can't undo by looking at the screen do.
 *
 * Read-only tools (`ui_snapshot_app`) never gate in either mode: they
 * observe, so pausing them buys no safety and costs a click.
 *
 * Entries WITHOUT `annotations` keep the legacy `readOnly`-only semantics —
 * an old client's snapshot must not suddenly start gating differently.
 */
export function uiToolCallNeedsApproval(opts: {
  readOnly: boolean;
  annotations?: UiToolAnnotations;
  requireToolApproval: boolean;
}): boolean {
  const { annotations, requireToolApproval } = opts;
  if (!annotations) {
    return requireToolApproval && !opts.readOnly;
  }
  // Destructive wins over everything, including a contradictory
  // `readOnlyHint: true`. The validator rejects `readOnlyHint` disagreeing
  // with `readOnly`, but nothing stops "read-only AND destructive" — and
  // resolving that contradiction in favor of "don't ask" is the one reading
  // that can silently delete something.
  if (annotations.destructiveHint === true) return true;
  // Read-only never gates. Honor BOTH signals: a partial annotation object
  // (e.g. `{destructiveHint: false}`) leaves `readOnlyHint` undefined, and
  // ignoring the legacy flag there would gate a snapshot for no reason.
  if (opts.readOnly || annotations.readOnlyHint === true) return false;
  if (requireToolApproval) return true;
  // Protocol default: absent destructiveHint means destructive.
  return annotations.destructiveHint !== false;
}

/**
 * Per-turn approval classification for the UI tools a turn advertised.
 *
 * Every validated entry lands in exactly one set, so an engine can answer
 * "does this UI tool call need approval?" without re-deriving policy — and
 * without the `requireToolApproval && …` shape that silently drops
 * destructive gating when the flag is off (the flag is off by default).
 *
 * `freeNames` is NOT redundant with "not in requiredNames": engines must
 * distinguish a UI tool that is deliberately approval-free from a name they
 * don't know about (a real MCP tool), which still follows the flag.
 */
export interface UiToolApprovalClassification {
  requiredNames: ReadonlySet<string>;
  freeNames: ReadonlySet<string>;
}

export function classifyUiToolApprovals(
  entries:
    | ReadonlyArray<{
        name: string;
        readOnly: boolean;
        annotations?: UiToolAnnotations;
      }>
    | undefined,
  requireToolApproval: boolean,
): UiToolApprovalClassification {
  const requiredNames = new Set<string>();
  const freeNames = new Set<string>();
  for (const entry of entries ?? []) {
    const target = uiToolCallNeedsApproval({
      readOnly: entry.readOnly,
      annotations: entry.annotations,
      requireToolApproval,
    })
      ? requiredNames
      : freeNames;
    target.add(entry.name);
  }
  return { requiredNames, freeNames };
}
